import { z } from 'zod';

import {
  createRuleDeprecatedSchema as createRuleSchema,
  GroupingSchema,
  SubRuleSchema,
  UtmItemDeprecatedSchema,
  UtmItemSchema,
  UtmOptionsEnum,
  UtmOptionsResponseEnum,
} from './taxonomy.js';

export const ActionEnum = z.enum(['add', 'remove']);

export const UtmItemResponseSchema = z.object({
  equals: z.string().optional(),
  is_dynamic_field: z.boolean().nullish(),
  contains: z.array(z.string()).optional(),
});

// Define the SubRuleSchema for the response format
export const SubRuleResponseSchema = z.partialRecord(
  UtmOptionsResponseEnum,
  UtmItemResponseSchema.nullable(),
);

export const SubRuleSchemaPayload = z.partialRecord(
  UtmOptionsEnum,
  UtmItemSchema.nullable(),
);

export const SubRuleDeprecatedSchemaPayload = z.partialRecord(
  UtmOptionsEnum,
  UtmItemDeprecatedSchema.nullable(),
);

export const PatchSingleRuleDeprecatedSchema = z.object({
  subRuleIndex: z.int().nonnegative(),
  subRulePayload: SubRuleDeprecatedSchemaPayload.nullable(),
  action: ActionEnum.optional(),
});

export const PatchSingleRuleSchema = z.object({
  subRuleIndex: z.int().nonnegative(),
  subRulePayload: SubRuleSchemaPayload.nullable(),
  action: ActionEnum.optional(),
});

export const RuleSchemaDeprecatedResponse = createRuleSchema(
  z.array(SubRuleResponseSchema),
);

export const RuleSchemaResponse = createRuleSchema(z.array(SubRuleSchema));

export const PatchSingleRuleSchemaDeprecatedResponse =
  RuleSchemaDeprecatedResponse;
export const PatchSingleRuleSchemaResponse = RuleSchemaResponse;

// UTM Builder

// Factory for SubRule schema
export function createSubRuleSchema<T extends z.ZodEnum>(UtmOptionsEnum: T) {
  return z.partialRecord(UtmOptionsEnum, UtmItemDeprecatedSchema.nullable());
}

// Factory for Rule schema
export function createRuleForUTMPayloadSchema<T extends z.ZodEnum>(
  UtmOptionsEnum: T,
) {
  return z.object({
    id: z.string(),
    sub_rules: z.array(createSubRuleSchema(UtmOptionsEnum)),
  });
}

// Factory for UTMBuilderPayload schema
export function createUTMBuilderPayloadSchema<T extends z.ZodEnum>(
  UtmOptionsEnum: T,
) {
  return z.object({
    platform: z.string(),
    channel: z.string(),
    rules: z.array(createRuleForUTMPayloadSchema(UtmOptionsEnum)),
  });
}

const RuleForUTMPayloadSchema = createRuleForUTMPayloadSchema(
  UtmOptionsResponseEnum,
);

export const UTMBuilderStoreSchema = createUTMBuilderPayloadSchema(
  UtmOptionsResponseEnum,
);

// Schema 2: with snake_case enum
export const UTMBuilderPayloadSchema =
  createUTMBuilderPayloadSchema(UtmOptionsEnum);

export const EqualsRulePayloadTaxonomyV3Schema = z.object({
  equals: z.string().max(400),
  isDynamicField: z.boolean().default(false).nullish(),
});

export const ContainsRulePayloadTaxonomyV3Schema = z.object({
  contains: z
    .array(
      z.object({
        value: z.string().max(400),
        isDynamicField: z.boolean().default(false).nullish(),
      }),
    )
    .max(5),
});

export const UtmValuePayloadSchema = EqualsRulePayloadTaxonomyV3Schema.or(
  ContainsRulePayloadTaxonomyV3Schema,
);

export const UTMBuilderRequestSchema = z.object({
  platformId: z.string(),
  ruleId: z.string(),
  subRuleIndex: z.number(),
  utmName: z.string().nullish(),
  utmValue: UtmValuePayloadSchema.nullish(),
  isDeleted: z.boolean().default(false).nullish(),
  modelConfig: z
    .object({
      extra: z.string().default('forbid'),
    })
    .nullish(),
});
export const postUTMBuilderQuerySchema = z.object({
  utmConfigId: z.string(),
});

export const SingleRuleUpdatedMessageSchema = z.object({
  id: z.string(),
  externalId: z.string().nullable(),
  grouping: GroupingSchema.nullable(),
  rule: RuleSchemaResponse.extend({
    utmConfigId: z.string().nullable(),
    platformId: z.string().nullable(),
    isValidToSave: z.boolean().nullish(),
  }),
});

export const StatusEnum = ['PENDING', 'FINISHED'] as const;
export const NameEnum = ['utm_builder', 'utm_validator'] as const;

// Zod enums from constants
export const StatusEnumSchema = z.enum(StatusEnum);
export const NameEnumSchema = z.enum(NameEnum);

// Type inference (optional)
export type RuleForUTMPayloadType = z.infer<typeof RuleForUTMPayloadSchema>;
export type UtmItemResponseType = z.infer<typeof UtmItemResponseSchema>;

export type UtmValuePayloadType = z.infer<typeof UtmValuePayloadSchema>;
export type UTMBuilderRequestType = z.infer<typeof UTMBuilderRequestSchema>;
export type UTMBuilderPayloadType = z.infer<typeof UTMBuilderPayloadSchema>;
export type UTMBuilderStoreType = z.infer<typeof UTMBuilderStoreSchema>;

export type ActionEnumType = z.infer<typeof ActionEnum>;
export type PatchSingleRuleResponseType = z.infer<
  typeof PatchSingleRuleSchemaDeprecatedResponse
>;
export type SingleRuleUpdatedMessageType = z.infer<
  typeof SingleRuleUpdatedMessageSchema
>;
